/**
 * What the board does around the player's actions (docs/ux.md §10.5, §11): captions over the
 * units after each action, the camera following the action (or a "Show" chip when the player
 * is moving the map), centring on a unit picked from a list, and pointing cells out.
 */
import type { GameEvent, GameState } from '@brig/rules-engine';
import type { Salvo } from '../board/scene.ts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Camera } from '../board/camera.ts';
import { cellOnScreen, centerOnCell, fitCells, follow } from '../board/focus.ts';
import type { ProjectionKind } from '../board/projection.ts';
import { captionsFor, useCaptions } from './captions.ts';
import { current } from './history.ts';
import type { TurnSnapshot } from './useTurn.ts';

/** How long pointed-out cells stay ringed, ms (U-LOG-02, U-FLEET-05). */
export const HIGHLIGHT_MS = 2000;

/** Where an action happened: its target, the ships it moved, the squadron the wind took. */
function focusOf(events: readonly GameEvent[], before: GameState, after: GameState): string | null {
  const cellOf = (s: GameState, id: string) =>
    s.ships.find((x) => x.id === id)?.cell ?? s.forts.find((f) => f.id === id)?.cell ?? null;
  for (const e of events) {
    if (e.type === 'shot') return cellOf(before, e.targetId);
    if (e.type === 'shipMoved' && e.mode === 'storm') return e.to;
    if (e.type === 'shipWrecked') return e.cell;
    if (e.type === 'shipMoved') return e.to;
  }
  const group = after.turn.group[0];
  return group ? cellOf(after, group) : null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function useBoardFeedback(snapshot: TurnSnapshot, camera: Camera, projection: ProjectionKind) {
  const { t } = useTranslation();
  const translate = t as unknown as Translate;
  const [captions, addCaptions] = useCaptions();
  const [highlight, setHighlight] = useState<ReadonlySet<string>>(new Set());
  const [showCell, setShowCell] = useState<string | null>(null);
  const [salvo, setSalvo] = useState<Salvo | null>(null);
  const salvos = useRef(0);
  const history = snapshot.context.history;
  const replay = snapshot.context.replay;

  // Each new action (or replay step): captions, and the camera follows.
  const seen = useRef(history.actions.length);
  const step = replay ? replay.at : history.states.length - 1;
  const lastStep = useRef(step);
  useEffect(() => {
    const grew = replay ? step > lastStep.current : history.actions.length > seen.current;
    lastStep.current = step;
    seen.current = history.actions.length;
    if (!grew || step < 1) return;
    const before = history.states[step - 1];
    const after = history.states[step];
    const events = history.events[step] ?? [];
    if (!before || !after) return;
    addCaptions(captionsFor(translate, events, before, after));
    // U-FIRE-09: the board plays the shots: smoke, the balls, a hit or a splash.
    const shots = events.flatMap((e) =>
      e.type === 'shot'
        ? [
            {
              shooters: e.shooters.map((s) => s.id),
              target: e.targetId,
              hit: e.hit,
              sank: events.some((x) => x.type === 'shipSunk' && x.shipId === e.targetId),
            },
          ]
        : [],
    );
    if (shots.length > 0) {
      salvos.current += 1;
      setSalvo({ key: salvos.current, shots });
    }
    const cell = focusOf(events, before, after);
    if (!cell) return;
    // The camera is the external system here: whether it could follow decides the chip.
    setShowCell(follow(camera, projection, cell) === 'held' ? cell : null);
    // Captions and follow react to the history only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, step]);

  // U-SEL-04: a unit picked anywhere comes into view.
  const selected = snapshot.context.selected;
  useEffect(() => {
    if (!selected) return;
    const state = current(history);
    const cell =
      state.ships.find((s) => s.id === selected)?.cell ?? state.forts.find((f) => f.id === selected)?.cell;
    if (cell && !cellOnScreen(camera, projection, cell)) centerOnCell(camera, projection, cell);
    // Only a new selection moves the camera, not every change of the game.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // The keyboard's cursor stays in view.
  const cursor = snapshot.context.cursor;
  useEffect(() => {
    if (cursor && !cellOnScreen(camera, projection, cursor, 32)) centerOnCell(camera, projection, cursor);
  }, [cursor, camera, projection]);

  useEffect(() => {
    if (highlight.size === 0) return;
    const timer = setTimeout(() => {
      setHighlight(new Set());
    }, HIGHLIGHT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [highlight]);

  /** Ring cells for a moment and bring them into view. */
  const pointOut = useCallback(
    (cells: readonly string[]) => {
      if (cells.length === 0) return;
      setHighlight(new Set(cells));
      fitCells(camera, projection, cells);
    },
    [camera, projection],
  );

  const show = useCallback(() => {
    if (showCell) centerOnCell(camera, projection, showCell);
    setShowCell(null);
  }, [camera, projection, showCell]);

  return { captions, highlight, pointOut, showCell, show, salvo };
}
